import { Stack } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import type { Report, ReportTemplate, ReportType } from "../../../../../../../api/types/svTypes.ts";
import useCurrentReportConfiguration from "../../../hooks/useCurrentReportConfiguration";
import { selectIsReportChanged } from "../../../store/selectors/currentReportSelectors";
import { selectFieldsStateIsConfigurationEmpty } from "../../../store/selectors/fieldsStateSelectors";
import { selectCompanies } from "../../../store/selectors/reportTemplatesSelectors";
import { selectEditDisabled } from "../../../store/selectors/uiSettingsSelectors.ts";
import { isNewReport } from "../../../utilities/Utilities.tsx";
import { VisualizationContextProvider } from "./context/VisualizationContextProvider.tsx";
import ReportTypesContainer from "./ReportTypesContainer.tsx";
import SaveReportChangesDialog from "./SaveReportChangesDialog.tsx";
import SaveTemplateChangesDialog from "./SaveTemplateChangesDialog.tsx";
import UnsavedChangesDialog from "./UnsavedChangesDialog.tsx";
import VisualizationLabel from "./VisualizationLabel.tsx";

type ReportEntity = Report | ReportTemplate;

export interface VisualizationProps<T extends ReportEntity> {
  report: T;
  update: (report: T) => Promise<T | undefined>;
  onSelect: (newType: ReportType) => void;
  onSaveChangesSaved?: (report: T) => void;
}

export default function Visualization<T extends ReportEntity>({
  report,
  update,
  onSelect,
  children,
  onSaveChangesSaved,
}: PropsWithChildren<VisualizationProps<T>>) {
  const isReportChanged = useSelector(selectIsReportChanged);
  const editDisabled = useSelector(selectEditDisabled);
  const companies = useSelector(selectCompanies);
  const [showUnsavedTooltip, setShowUnsavedTooltip] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newType, setNewType] = useState<ReportType>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const isConfigurationEmpty = useSelector(selectFieldsStateIsConfigurationEmpty);
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const handleReportTypeClick = useCallback(
    (newType: ReportType, ref: HTMLButtonElement | null) => {
      if (report.reportType === newType) return;
      if (isConfigurationEmpty === false && isReportChanged === true && ref !== null) {
        setNewType(newType);
        setAnchorEl(ref);
        setShowUnsavedTooltip(true);
      } else {
        onSelect(newType);
      }
    },
    [report.reportType, isReportChanged, onSelect, isConfigurationEmpty]
  );

  const handleCloseUnsavedChangesDialog = useCallback(() => {
    setShowUnsavedTooltip(false);
    setAnchorEl(undefined);
  }, []);

  const handleSave = useCallback(async () => {
    if (!report) return;
    const reportConfiguration = await getCurrentReportConfigurationAsync();
    if (reportConfiguration === undefined) {
      return;
    }
    const reportToUpdate = structuredClone(report);
    reportToUpdate.configuration = reportConfiguration;

    if (isNewReport(reportToUpdate)) {
      setShowSaveDialog(true);
    } else {
      const updatedReport = await update(reportToUpdate);
      if (!updatedReport) {
        return;
      }
    }
    handleCloseUnsavedChangesDialog();
  }, [update, report, getCurrentReportConfigurationAsync, handleCloseUnsavedChangesDialog]);

  const handleContinue = useCallback(() => {
    if (newType !== undefined) onSelect(newType);
    setShowUnsavedTooltip(false);
    setAnchorEl(undefined);
  }, [newType, onSelect]);

  const handleCloseSaveChangesDialog = useCallback(() => {
    setShowSaveDialog(false);
  }, []);

  const handleSaveChangesSaved = useCallback(
    (report: ReportEntity) => {
      handleCloseSaveChangesDialog();
      onSaveChangesSaved?.(report as T);
    },
    [handleCloseSaveChangesDialog, onSaveChangesSaved]
  );

  if (!report) return null;

  return (
    <VisualizationContextProvider
      report={report}
      editDisabled={editDisabled}
      companies={companies}
      isReportChanged={isReportChanged}
      onReportTypeClicked={handleReportTypeClick}
      isConfigurationEmpty={isConfigurationEmpty}
      showUnsavedTooltip={showUnsavedTooltip}
      showSaveDialog={showSaveDialog}
      unsavedChangesPopoverAnchor={anchorEl}
      onSaveChange={handleSave}
      onContinueUnsavedChanges={handleContinue}
      onCloseUnsavedChanges={handleCloseUnsavedChangesDialog}
      onCloseSaveChanges={handleCloseSaveChangesDialog}
      onSaveChangesSaved={handleSaveChangesSaved}
      getCurrentReportConfigurationAsync={getCurrentReportConfigurationAsync}
    >
      <Stack sx={{ gap: 2 }}>{children}</Stack>
    </VisualizationContextProvider>
  );
}

Visualization.Label = VisualizationLabel;
Visualization.ReportTypesContainer = ReportTypesContainer;
Visualization.UnsavedChangesPopover = UnsavedChangesDialog;
Visualization.SaveReportDialog = SaveReportChangesDialog;
Visualization.SaveTemplateDialog = SaveTemplateChangesDialog;
